/**
 * Liste devis · éditeur (clic ligne). Validé = lecture seule.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { facturxFetch } from "../api/client";
import { useHubBack } from "../mobile/hubBack";
import { Button } from "../components/Button";
import { ConfirmModal } from "../components/ConfirmModal";
import { PageHeader } from "../components/PageHeader";
import { asProduct, type Product, type TaxRate } from "../features/catalog/types";
import { QuotesListBar } from "../features/quotes/QuotesListBar";
import { docFilterChips, docMatchesFilter } from "../features/quotes/docFilters";
import { QuotesTable } from "../features/quotes/QuotesTable";
import { QuoteEditor } from "../features/quotes/QuoteEditor";
import { QuotePdfDrawer } from "../features/quotes/QuotePdfDrawer";
import { QuoteTraceDrawer } from "../features/quotes/QuoteTraceDrawer";
import { openQuoteArchive, printQuotePdf, quotePrintUrl } from "../features/quotes/quotePdf";
import { docKindNone, docKindNoun, docKindTitle, quoteHashQuery, quoteMatches } from "../features/quotes/format";
import { workflowFromCompany } from "../workflow/types";
import {
  addDays,
  draftToPayload,
  emptyDraft,
  lineFromProduct,
  lineKeptWithoutLabel,
  quoteToDraft,
  todayIso,
  type DocKind,
  type Quote,
  type QuoteDraft,
} from "../features/quotes/types";
import { asGcCharge, gcIsOpen } from "../payments/gocardless";
import { connectedGateways, type GatewayProvider } from "../payments/gateways";
import { PayModal } from "../features/quotes/PayModal";
import { CancelInvoiceModal } from "../features/quotes/CancelInvoiceModal";
import { SendMailSheet } from "../features/quotes/SendMailSheet";
import type { ClientListItem } from "../features/clients/types";
import { formatsFromCompany } from "../formats/types";
import { useFormatters } from "../formats/useFormatters";
import { useCompany } from "../company/CompanyProvider";

export type QuoteSeed = { productId?: number; clientId?: number; quoteId?: number; filter?: string };

function listOnlyFilter(seed: QuoteSeed | null | undefined): string | null {
  if (!seed?.filter || seed.quoteId || seed.clientId || seed.productId) return null;
  return seed.filter;
}
export type { DocKind };

type QuotesPageProps = {
  kind?: DocKind;
  companyId: number;
  seed?: QuoteSeed | null;
  onSeedUsed?: () => void;
  onOpenClient?: (clientId: number) => void;
  onOpenInvoice?: (id: number) => void;
  onOpenQuote?: (id: number) => void;
  onOpenCredit?: (id: number) => void;
  onOpenRecurring?: (fromDocumentId: number) => void;
};

type ConfirmKind = "del" | "convert" | "collect";

function asQuote(row: Quote): Quote {
  return {
    ...row,
    id: Number(row.id),
    company_id: Number(row.company_id),
    client_id: Number(row.client_id),
    doc_type: row.doc_type === "invoice" ? "invoice" : row.doc_type === "credit_note" ? "credit_note" : "quote",
    total_ht_cents: Number(row.total_ht_cents) || 0,
    total_tax_cents: Number(row.total_tax_cents) || 0,
    total_ttc_cents: Number(row.total_ttc_cents) || 0,
    discount_bp: Number(row.discount_bp) || 0,
    discount_cents: Number(row.discount_cents) || 0,
    converted_invoice_id: row.converted_invoice_id ? Number(row.converted_invoice_id) : null,
    credit_note_id: row.credit_note_id ? Number(row.credit_note_id) : null,
    credited: row.credited === true,
    viewed_at: row.viewed_at && row.viewed_at !== "0000-00-00 00:00:00" ? String(row.viewed_at) : null,
    content_hash: row.content_hash ? String(row.content_hash).toLowerCase() : null,
    proof_hash: row.proof_hash ? String(row.proof_hash).toLowerCase() : null,
    paid_cents: Number(row.paid_cents) || 0,
    remaining_cents: (Number(row.total_ttc_cents) || 0) - (Number(row.paid_cents) || 0),
    paid: row.paid === true || ((Number(row.paid_cents) || 0) > 0 && (Number(row.paid_cents) || 0) >= (Number(row.total_ttc_cents) || 0) && (Number(row.total_ttc_cents) || 0) > 0),
    overdue: row.overdue === true,
    gc_charge: asGcCharge(row.gc_charge),
    archived: row.archived === true,
    archive_rel: row.archive_rel ?? null,
  };
}

export function QuotesPage({ kind = "quote", companyId, seed, onSeedUsed, onOpenClient, onOpenInvoice, onOpenQuote, onOpenCredit, onOpenRecurring }: QuotesPageProps) {
  const isInv = kind === "invoice";
  const isCredit = kind === "credit_note";
  const { company } = useCompany();
  const { money } = useFormatters();
  const decimalComma = formatsFromCompany(company).decimal_comma;
  const validDays = kind === "quote" ? Number(company?.quote_valid_days ?? 30) : 30;
  const legal = legalFor(kind);

  function blankDraft() {
    return emptyDraft(validDays, defaultTaxId, decimalComma, legal);
  }
  const [rows, setRows] = useState<Quote[]>([]);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [defaultTaxId, setDefaultTaxId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const [filter, setFilter] = useState(() => listOnlyFilter(seed) || "all");
  const [view, setView] = useState<"list" | "form">("list");
  const [draft, setDraft] = useState<QuoteDraft | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [linked, setLinked] = useState<ReadonlySet<GatewayProvider>>(() => new Set());
  const [confirm, setConfirm] = useState<{ kind: ConfirmKind; id: number; label: string } | null>(null);
  const [sendDoc, setSendDoc] = useState<Quote | null>(null);
  const [sendError, setSendError] = useState("");
  const [payRow, setPayRow] = useState<Quote | null>(null);
  const [cancelRow, setCancelRow] = useState<Quote | null>(null);
  const [pdfQuote, setPdfQuote] = useState<Quote | null>(null);
  const [traceQuote, setTraceQuote] = useState<Quote | null>(null);
  const pdfFrame = useRef<HTMLIFrameElement>(null);
  const openedHash = useRef("");
  const overlay = Boolean(traceQuote || pdfQuote || sendDoc || payRow || cancelRow || confirm);
  useHubBack((view === "form" ? 1 : 0) + (overlay ? 1 : 0), () => {
    if (traceQuote) {
      setTraceQuote(null);
      return;
    }
    if (pdfQuote) {
      setPdfQuote(null);
      return;
    }
    if (sendDoc) {
      setSendDoc(null);
      setSendError("");
      return;
    }
    if (payRow) {
      setPayRow(null);
      return;
    }
    if (cancelRow) {
      setCancelRow(null);
      return;
    }
    if (confirm) {
      setConfirm(null);
      return;
    }
    setError("");
    setDraft(null);
    setView("list");
  });

  const load = useCallback(async () => {
    const params = new URLSearchParams({ company_id: String(companyId), doc_type: kind });
    return facturxFetch<{ documents: Quote[] }>(`docs-list.php?${params}`);
  }, [companyId, kind]);

  const visible = useMemo(() => {
    const wf = workflowFromCompany(company);
    return rows.filter((r) => {
      if (!quoteMatches(r, q)) return false;
      if (!docMatchesFilter(r, kind, filter)) return false;
      if (r.shelved_at) return false;
      if (kind === "quote" && filter !== "converted" && wf.wf_archive_quote && r.converted_invoice_id) return false;
      if (kind === "invoice" && filter !== "paid" && wf.wf_archive_paid && r.paid) return false;
      return true;
    });
  }, [rows, q, filter, kind, company]);

  useEffect(() => {
    const hash = quoteHashQuery(q);
    if (!hash) {
      openedHash.current = "";
      return;
    }
    if (visible.length !== 1 || openedHash.current === hash) return;
    openedHash.current = hash;
    setTraceQuote(visible[0]);
  }, [q, visible]);

  async function refresh() {
    const data = await load();
    const next = (data.documents || []).map(asQuote);
    setRows(next);
    return next;
  }

  useEffect(() => {
    setView("list");
    setDraft(null);
    setQ("");
    setFilter(listOnlyFilter(seedRef.current) || "all");
    setMenuId(null);
    setConfirm(null);
    setPdfQuote(null);
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    setLinked(new Set());
    facturxFetch<{ gateways: { provider: GatewayProvider; connected: boolean; status: string }[] }>(
      `payment-gateways.php?company_id=${companyId}`,
    )
      .then((data) => {
        if (!stop) setLinked(connectedGateways(data.gateways));
      })
      .catch(() => {
        if (!stop) setLinked(new Set());
      });
    return () => {
      stop = true;
    };
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    Promise.all([
      facturxFetch<{ tax_rates: TaxRate[]; default_tax_rate_id: number | null }>(`tax-rates.php?company_id=${companyId}`),
      facturxFetch<{ clients: ClientListItem[] }>(`clients.php?company_id=${companyId}`),
    ])
      .then(([tax, cl]) => {
        if (stop) return;
        setRates((tax.tax_rates || []).map((r) => ({ ...r, id: Number(r.id), rate_bp: Number(r.rate_bp) })));
        setDefaultTaxId(tax.default_tax_rate_id === null ? null : Number(tax.default_tax_rate_id));
        setClients(cl.clients || []);
      })
      .catch((e) => {
        if (!stop) setError(e instanceof Error ? e.message : "Erreur");
      });
    return () => {
      stop = true;
    };
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    const pull = () =>
      load()
        .then((data) => {
          if (!stop) setRows((data.documents || []).map(asQuote));
        })
        .catch((e) => {
          if (!stop) setError(e instanceof Error ? e.message : "Erreur");
        });
    setError("");
    void pull();
    const onVis = () => {
      if (document.visibilityState === "visible") void pull();
    };
    window.addEventListener("focus", pull);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop = true;
      window.removeEventListener("focus", pull);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  useEffect(() => {
    if (!seed) return;
    const only = listOnlyFilter(seed);
    if (only) {
      setFilter(only);
      setView("list");
      setDraft(null);
      onSeedUsed?.();
      return;
    }
    void startFromSeed(seed);
    onSeedUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  async function startFromSeed(s: QuoteSeed) {
    if (listOnlyFilter(s)) return;
    if (s.quoteId) {
      await openEdit(s.quoteId);
      return;
    }
    const base = blankDraft();
    if (s.clientId) base.client_id = s.clientId;
    if (s.productId) {
      try {
        const data = await facturxFetch<{ product: Product }>(`products.php?id=${s.productId}`);
        base.lines = [lineFromProduct(asProduct(data.product), decimalComma)];
      } catch {
        /* ligne vide */
      }
    }
    setError("");
    setDraft(base);
    setView("form");
  }

  async function openEdit(id: number) {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ document: Quote }>(`documents.php?id=${id}`);
      setDraft(quoteToDraft(asQuote(data.document), decimalComma, defaultTaxId, legal));
      setView("form");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function legalFor(target: DocKind) {
    if (target === "invoice") {
      return { terms_html: company?.invoice_terms || "", footer_html: company?.invoice_footer || "" };
    }
    if (target === "credit_note") {
      return { terms_html: company?.credit_terms || "", footer_html: company?.credit_footer || "" };
    }
    return { terms_html: company?.quote_terms || "", footer_html: company?.quote_footer || "" };
  }

  async function openClone(id: number, target: DocKind) {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ document: Quote }>(`documents.php?id=${id}`);
      const d = quoteToDraft(asQuote(data.document), decimalComma, defaultTaxId, legalFor(target));
      delete d.id;
      d.status = "draft";
      d.number = null;
      d.immutable = false;
      d.signature = null;
      d.converted_invoice_id = null;
      d.paid = false;
      d.paid_cents = 0;
      d.issue_date = todayIso();
      d.operation_date = d.issue_date;
      d.due_date = addDays(d.issue_date, target === "invoice" ? 30 : Number(company?.quote_valid_days ?? 30));
      const created = await facturxFetch<{ document: Quote }>("documents.php", {
        method: "POST",
        body: JSON.stringify(draftToPayload(d, companyId, decimalComma, target)),
      });
      const newId = Number(created.document.id);
      if (target !== kind) {
        if (target === "invoice") onOpenInvoice?.(newId);
        else if (target === "credit_note") onOpenCredit?.(newId);
        else onOpenQuote?.(newId);
        return;
      }
      await openEdit(newId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function refreshGc(id: number) {
    setBusy(true);
    setError("");
    try {
      await facturxFetch(`gocardless-charge.php?document_id=${id}&action=refresh`, {
        method: "POST",
        body: JSON.stringify({ document_id: id }),
      });
      await refresh();
      if (draft?.id === id) await openEdit(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function savePay(p: { paid_at: string; amount_cents: number; method: string; reference: string; note: string }) {
    if (!payRow) return;
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ document: Quote }>("payments.php", {
        method: "POST",
        body: JSON.stringify({ document_id: payRow.id, ...p }),
      });
      setPayRow(null);
      await refresh();
      if (draft?.id === payRow.id) {
        setDraft(quoteToDraft(asQuote(data.document), decimalComma, defaultTaxId, legal));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function applyCredit() {
    if (!cancelRow) return;
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ document: Quote }>(`documents.php?id=${cancelRow.id}&action=credit`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setCancelRow(null);
      await refresh();
      const newId = Number(data.document.id);
      if (onOpenCredit) {
        onOpenCredit(newId);
        return;
      }
      await openEdit(newId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function goList() {
    setError("");
    setDraft(null);
    setView("list");
  }

  async function save() {
    if (!draft) return;
    if (!draft.client_id) {
      setError("Le client est obligatoire.");
      return;
    }
    if (draft.lines.some((l) => lineKeptWithoutLabel(l, decimalComma))) {
      setError("Chaque ligne doit avoir une désignation.");
      return;
    }
    const body = draftToPayload(draft, companyId, decimalComma, kind);
    if (body.lines.length === 0 && draft.immutable === false) {
      /* on autorise l’enregistrement vide en brouillon */
    }
    setBusy(true);
    setError("");
    try {
      if (draft.id) {
        const data = await facturxFetch<{ document: Quote }>(`documents.php?id=${draft.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        setDraft(quoteToDraft(asQuote(data.document), decimalComma, defaultTaxId, legal));
      } else {
        const data = await facturxFetch<{ document: Quote }>("documents.php", {
          method: "POST",
          body: JSON.stringify(body),
        });
        setDraft(quoteToDraft(asQuote(data.document), decimalComma, defaultTaxId, legal));
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function applyConfirm() {
    if (!confirm) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (confirm.kind === "del") {
        await facturxFetch(`documents.php?id=${confirm.id}`, { method: "DELETE" });
        setConfirm(null);
        setDraft(null);
        await refresh();
        setView("list");
      } else if (confirm.kind === "collect") {
        await facturxFetch(`gocardless-charge.php`, {
          method: "POST",
          body: JSON.stringify({ document_id: confirm.id }),
        });
        setConfirm(null);
        setNotice("Prélèvement lancé. « Payé » se pose tout seul quand GoCardless confirme.");
        await refresh();
        if (draft?.id === confirm.id) await openEdit(confirm.id);
      } else {
        const conv = await facturxFetch<{ document: Quote }>(`documents.php?id=${confirm.id}&action=convert`, {
          method: "POST",
          body: JSON.stringify({}),
        });
        setConfirm(null);
        await refresh();
        if (conv.document?.id && onOpenInvoice) {
          onOpenInvoice(Number(conv.document.id));
          return;
        }
        if (draft?.id === confirm.id) await openEdit(confirm.id);
      }
    } catch (e) {
      setConfirm(null);
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function beginSend(id: number) {
    const row = rows.find((r) => r.id === id);
    const fromDraft =
      draft?.id === id
        ? ({
            id,
            company_id: companyId,
            client_id: draft.client_id,
            client_name: clients.find((c) => c.id === draft.client_id)?.name || "",
            doc_type: kind,
            status: draft.immutable ? "validated" : draft.status || "draft",
            number: draft.number,
            issue_date: draft.issue_date,
            due_date: draft.due_date,
            total_ht_cents: 0,
            total_tax_cents: 0,
            total_ttc_cents: draft.total_ttc_cents || row?.total_ttc_cents || 0,
            archived: draft.archived === true,
            signed_at: draft.signature?.signed_at || null,
          } satisfies Quote)
        : null;
    const doc = row || fromDraft;
    if (!doc) return;
    setSendError("");
    setSendDoc(doc);
  }

  async function applySend(mail: { subject: string; body: string }) {
    if (!sendDoc) return;
    setBusy(true);
    setSendError("");
    setNotice("");
    try {
      const data = await facturxFetch<{ document: Quote; sent?: boolean; to?: string; message?: string }>(
        `documents.php?id=${sendDoc.id}&action=send`,
        { method: "POST", body: JSON.stringify({ mail_subject: mail.subject, mail_body: mail.body }) },
      );
      const sentId = sendDoc.id;
      setSendDoc(null);
      setDraft(quoteToDraft(asQuote(data.document), decimalComma, defaultTaxId, legal));
      if (data.sent === false && data.message) setError(data.message);
      else if (data.sent && data.to) setNotice(`E-mail envoyé à ${data.to}.`);
      await refresh();
      if (view === "form") await openEdit(sentId);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  const sendClient = sendDoc ? clients.find((c) => c.id === sendDoc.client_id) : undefined;
  const viewKey = view === "form" ? `form-${draft?.id ?? "new"}` : "list";

  return (
    <div className="w-full">
      {notice && view !== "form" ? <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p> : null}
      {error && view !== "form" ? <p className="mb-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p> : null}
      <div className="fx-page" key={viewKey}>
        {view === "list" ? (
          <>
            <PageHeader
              title={docKindTitle(kind, true)}
              action={
                isCredit ? undefined : (
                  <Button
                    onClick={() => {
                      setError("");
                      setDraft(blankDraft());
                      setView("form");
                    }}
                    disabled={busy}
                  >
                    {kind === "invoice" ? "Nouvelle facture" : "Nouveau devis"}
                  </Button>
                )
              }
            />
            <QuotesListBar
              q={q}
              onQ={setQ}
              count={visible.length}
              unit={docKindNoun(kind, true)}
              filters={docFilterChips(kind)}
              filter={filter}
              onFilter={setFilter}
            />
            <QuotesTable
              kind={kind}
              rows={visible}
              emptyText={q.trim() || filter !== "all" ? `${docKindNone(kind)} ne correspond.` : `${docKindNone(kind)}.`}
              menuId={menuId}
              onMenu={setMenuId}
              onOpen={(id) => {
                if (!busy) void openEdit(id);
              }}
              onOpenClient={onOpenClient}
              onCloneQuote={(id) => void openClone(id, "quote")}
              onCloneInvoice={(id) => void openClone(id, "invoice")}
              onCloneRecurring={onOpenRecurring}
              onPay={isInv ? (row) => setPayRow(row) : undefined}
              linked={linked}
              onCollect={isInv ? (row) => setConfirm({ kind: "collect", id: row.id, label: row.number || row.client_name || "cette facture" }) : undefined}
              onGcRefresh={
                isInv
                  ? (row) => {
                      void refreshGc(row.id);
                    }
                  : undefined
              }
              onSend={(row) => beginSend(row.id)}
              onPdf={(row) => setPdfQuote(row)}
              onPrint={(row) => printQuotePdf(row.id, undefined, row.number, row.archived === true, Boolean(row.signed_at || row.signature))}
              onArchive={(row) => openQuoteArchive(row.id)}
              onTrace={(row) => setTraceQuote(row)}
              onConvert={!isInv && !isCredit ? (row) => setConfirm({ kind: "convert", id: row.id, label: row.number || "ce devis" }) : undefined}
              onCredit={isInv ? (row) => setCancelRow(row) : undefined}
              onOpenCredit={onOpenCredit}
              onDelete={(row) => setConfirm({ kind: "del", id: row.id, label: row.number || row.client_name || `ce ${docKindNoun(kind)}` })}
            />
            {pdfQuote ? (
              <QuotePdfDrawer
                src={quotePrintUrl(pdfQuote.id)}
                frameRef={pdfFrame}
                onPrint={() => printQuotePdf(pdfQuote.id, pdfFrame.current, pdfQuote.number, pdfQuote.archived === true, Boolean(pdfQuote.signed_at || pdfQuote.signature))}
                onClose={() => setPdfQuote(null)}
              />
            ) : null}
            {traceQuote ? <QuoteTraceDrawer id={traceQuote.id} onClose={() => setTraceQuote(null)} /> : null}
          </>
        ) : null}
        {view === "form" && draft ? (
          <QuoteEditor
            kind={kind}
            companyId={companyId}
            draft={draft}
            setDraft={setDraft}
            clients={clients}
            rates={rates}
            decimalComma={decimalComma}
            defaultTaxId={defaultTaxId}
            busy={busy}
            error={error}
            notice={notice}
            onSave={() => void save()}
            onCancel={goList}
            onSend={draft.id ? () => beginSend(draft.id as number) : undefined}
            onCloneQuote={draft.id ? () => void openClone(draft.id as number, "quote") : undefined}
            onCloneInvoice={draft.id ? () => void openClone(draft.id as number, "invoice") : undefined}
            onCloneRecurring={draft.id && onOpenRecurring ? () => onOpenRecurring(draft.id as number) : undefined}
            linked={linked}
            onPay={
              isInv && draft.id && draft.immutable && !draft.paid && !draft.credited
                ? () =>
                    setPayRow({
                      id: draft.id as number,
                      company_id: companyId,
                      client_id: draft.client_id,
                      doc_type: "invoice",
                      status: draft.status,
                      number: draft.number,
                      issue_date: draft.issue_date,
                      due_date: draft.due_date,
                      total_ht_cents: 0,
                      total_tax_cents: 0,
                      total_ttc_cents: draft.total_ttc_cents || rows.find((r) => r.id === draft.id)?.total_ttc_cents || 0,
                      paid_cents: draft.paid_cents || 0,
                      paid: draft.paid,
                    })
                : undefined
            }
            onCollect={
              isInv && draft.id && draft.immutable && !draft.paid && !draft.credited && !gcIsOpen(rows.find((r) => r.id === draft.id) || null)
                ? () => setConfirm({ kind: "collect", id: draft.id as number, label: draft.number || "cette facture" })
                : undefined
            }
            onGcRefresh={
              isInv && draft.id && gcIsOpen(rows.find((r) => r.id === draft.id) || null)
                ? () => void refreshGc(draft.id as number)
                : undefined
            }
            onConvert={!isInv && !isCredit && draft.id ? () => setConfirm({ kind: "convert", id: draft.id as number, label: draft.number || "ce devis" }) : undefined}
            onCredit={isInv && draft.id && draft.immutable && !draft.credited ? () => setCancelRow(rows.find((r) => r.id === draft.id) || {
              id: draft.id as number,
              company_id: companyId,
              client_id: draft.client_id,
              doc_type: "invoice",
              status: draft.status,
              number: draft.number,
              issue_date: draft.issue_date,
              due_date: draft.due_date,
              total_ht_cents: 0,
              total_tax_cents: 0,
              total_ttc_cents: draft.total_ttc_cents || 0,
            }) : undefined}
            onOpenCredit={isInv && draft.credit_note_id ? () => onOpenCredit?.(draft.credit_note_id as number) : undefined}
            onDelete={draft.id ? () => setConfirm({ kind: "del", id: draft.id as number, label: draft.number || `ce ${docKindNoun(kind)}` }) : undefined}
          />
        ) : null}
      </div>
      <ConfirmModal
        open={confirm !== null}
        title={
          confirm?.kind === "convert"
            ? "Convertir en facture ?"
            : confirm?.kind === "collect"
              ? "Prélever cette facture ?"
              : "Supprimer ce brouillon ?"
        }
        message={
          confirm
            ? confirm.kind === "convert"
              ? `« ${confirm.label} » ouvrira un brouillon de facture — jamais une pièce validée toute seule.`
              : confirm.kind === "collect"
                ? `On prélève le solde TTC de « ${confirm.label} » chez le client (mandat SEPA). « Payé » se pose tout seul à la confirmation GoCardless.`
                : `« ${confirm.label} » sera effacé.`
            : undefined
        }
        confirmVariant={confirm?.kind === "del" ? "danger" : "primary"}
        confirmLabel={confirm?.kind === "del" ? "Supprimer" : confirm?.kind === "convert" ? "Convertir" : "Prélever"}
        busyLabel={confirm?.kind === "del" ? "Suppression…" : confirm?.kind === "convert" ? "Conversion…" : "Prélèvement…"}
        busy={busy}
        onCancel={() => {
          if (!busy) setConfirm(null);
        }}
        onConfirm={() => void applyConfirm()}
      />
      {sendDoc ? (
        <SendMailSheet
          doc={sendDoc}
          clientName={sendDoc.client_name || sendClient?.name || ""}
          clientEmail={(sendClient?.email || "").trim()}
          busy={busy}
          error={sendError}
          onCancel={() => {
            if (!busy) {
              setSendDoc(null);
              setSendError("");
            }
          }}
          onSend={(mail) => void applySend(mail)}
        />
      ) : null}
      <PayModal
        open={payRow !== null}
        label={payRow?.number || payRow?.client_name || "cette facture"}
        remainingCents={Math.max(0, (payRow?.total_ttc_cents || 0) - (payRow?.paid_cents || 0))}
        decimalComma={decimalComma}
        busy={busy}
        onCancel={() => {
          if (!busy) setPayRow(null);
        }}
        onConfirm={(p) => void savePay(p)}
      />
      <CancelInvoiceModal
        open={cancelRow !== null}
        number={cancelRow?.number || cancelRow?.client_name || "cette facture"}
        amount={cancelRow ? money(cancelRow.total_ttc_cents) : ""}
        busy={busy}
        onCancel={() => {
          if (!busy) setCancelRow(null);
        }}
        onConfirm={() => void applyCredit()}
      />
    </div>
  );
}
